import { action, computed, IReactionDisposer, makeObservable, observable, reaction, runInAction } from 'mobx';
import last from 'lodash/last';
import debounce from 'lodash/debounce';
import { assertNotNull, AsyncResult, Maybe } from '../core';
import { Timer } from '../core/timer';
import { Store } from '../store/store';
import { BindingProps, StoreNode } from '../store';
import { ApiResult, ApiVariables } from '../api/apiSchema';
import { GetJobsFilterInput, JobOrderByOption, JobSourceType, JobStatus, JobType, Metadata, Order, PageInfo } from '@clipr/lib';
import { SyncStatus } from '../store/syncSchema';
import { JobModel } from './job';
import { SortFieldValue } from '../components/input/sortFieldBtn';
import { JobCatalogItem } from './jobCatalogItem';
import { JobsPosterSize } from './jobManager';
import { getVideoTypeLabel, JobLiveAggregateStatus } from '.';
import { input, InputState } from '../components';
import { JobFilter } from './jobSchema';
import { getCatalogMetadataFilterInput, getCatalogTagsFilterInput } from './catalogUtils';

const PROCESSING_JOBS_REFRESH_INTERVAL = 4000;
// ponytail: ~12 minutes of list refetches for a just-uploaded job (QA saw an 8-minute index delay); after that a manual refresh is needed.
const MAX_TRANSIENT_JOB_REFETCHES = 150;
export const MAX_COMBINED_SOURCES = 5;

type Props = BindingProps<{
  teamId?: string;
  pageSize?: number;
  allJobs?: boolean;
  persistSort?: boolean;
  /** The library root lists only unfiled jobs, unless a search or filter widens it to the whole library. */
  folders?: boolean;
}> & {
  /**
   * Local filter to apply to all Job entities.
   * Currently it's only being used to detect the transient entities.
   */
  jobFilter?: JobFilter;
};

export class JobCatalogSource
  extends StoreNode {

  readonly nodeType: 'JobCatalogSource' = 'JobCatalogSource';

  constructor(store: Store, props?: Props) {
    super(store, props);

    this.pageSize = this.props.pageSize ?? 20;
    makeObservable<this, 'transientJobRefetches' | 'fetchRaw' | 'isWaitingForTransientJobs'>(this, {
      teamId: computed,
      allJobs: computed,
      sortStorageKey: computed,
      jobFilter: computed,
      transientJobRefetches: observable,
      pageSize: observable,
      syncStatus: observable,
      statusFilter: observable,
      liveStatusFilter: observable,
      videoTypeFilter: observable,
      createdByIdFilter: observable,
      sourceTypeFilter: observable,
      metadataFilter: observable,
      tagsFilter: observable,
      folderId: observable,
      hasRefinement: computed,
      searchFilter: observable,
      sortField: observable,
      sortOrder: observable,
      pageInfo: observable,
      lastItemCursor: computed,
      itemEntityIds: computed,
      itemEntities: computed,
      transientEntityIds: computed,
      transientEntities: computed,
      entities: computed,
      processingJobIds: computed,
      isEndOfList: computed,
      setSortFieldValue: action,
      restoreSort: action,
      setFilterFieldValue: action,
      setLiveStatusFilterValue: action,
      handleSearchInputChange: action,
      setPagesizeValue: action,
      setVideoTypeFilterValue: action,
      fetch: action,
      fetchMore: action,
      fetchRaw: action,
      reset: action,
      isWaitingForTransientJobs: computed,
      selectedJobs: computed,
      toggleSelection: action,
      clearSelection: action,
    });

    this.searchBarModel = input(this, {
      name: 'searchInput',
      onChange: (e) => this.handleSearchInputChange(e),
    });
  }

  // #region Resolved props
  // -------

  get teamId(): string | null {
    return this.resolvedProps.teamId ?? null;
  }

  get allJobs(): boolean {
    return this.resolvedProps.allJobs ?? false;
  }

  get sortStorageKey(): string | null {
    return this.resolvedProps.persistSort && this.teamId ? `library.sort.${this.teamId}` : null;
  }

  get jobFilter(): JobFilter | null {
    return this.getProp('jobFilter');
  }

  // #endregion

  readonly searchBarModel: InputState;
  readonly items = observable.array<JobCatalogItem>();
  readonly selectedJobIds = observable.set<string>();
  private readonly processingJobsRefreshTimer = new Timer({
    callback: () => this.refreshProcessingJobs(),
    timeout: PROCESSING_JOBS_REFRESH_INTERVAL,
  });
  private processingJobsAbortController: AbortController | null = null;
  private isRefreshingProcessingJobs = false;
  private transientJobRefetches = 0;
  private transientJobsReaction: IReactionDisposer | null = null;

  pageSize: number;
  syncStatus: SyncStatus = 'idle';

  statusFilter: JobStatus | null = null;
  liveStatusFilter: JobLiveAggregateStatus | null = null;
  videoTypeFilter: string | null = null;
  createdByIdFilter: string | null = null;
  sourceTypeFilter: JobSourceType | null = null;

  metadataFilter: Partial<Metadata> | null = null;
  tagsFilter: string[] | null = null;
  folderId: string | null = null;

  searchFilter: string | null = null;
  sortField: JobOrderByOption | null = 'createdAt';
  sortOrder: Order | null = 'desc';

  pageInfo: PageInfo | null = null;

  /** A search, tag or metadata filter, which looks across the whole library instead of one folder level. */
  get hasRefinement(): boolean {
    return !!(this.searchFilter
      || getCatalogTagsFilterInput(this.tagsFilter)
      || getCatalogMetadataFilterInput(this.metadataFilter));
  }

  get lastItemCursor(): string | null {
    return last(this.items)?.cursor || null;
  }

  get itemEntityIds(): Set<string> {
    return new Set(this.itemEntities.map(job => job.id));
  }

  get itemEntities(): JobModel[] {
    return this.items.map(item => item.job);
  }

  get transientEntityIds(): Set<string> {
    return new Set(this.transientEntities.map(job => job.id));
  }

  get transientEntities(): JobModel[] {
    const filter = this.jobFilter;
    if (typeof filter !== 'function')
      return [];

    return this.store.jobManager.transientJobs.filter(job => filter(job));
  }

  get entities(): JobModel[] {

    this._verifyEntityDuplicates();

    return [
      ...this.transientEntities,
      ...this.itemEntities
    ];
  }

  get processingJobIds(): string[] {
    return this.itemEntities
      .filter(job => job.isMediaProcessing || job.isInProgress)
      .map(job => job.id);
  }

  get selectedJobs(): JobModel[] {
    return [...this.selectedJobIds]
      .map(id => this.store.jobManager.maybeGetJob(id))
      .filter((job): job is JobModel => !!job);
  }

  canSelect(job: JobModel): boolean {
    return job.isDone && !job.isCombinedReport;
  }

  toggleSelection(job: JobModel) {
    if (this.selectedJobIds.has(job.id))
      this.selectedJobIds.delete(job.id);
    else if (this.canSelect(job) && this.selectedJobIds.size < MAX_COMBINED_SOURCES)
      this.selectedJobIds.add(job.id);
  }

  clearSelection() {
    this.selectedJobIds.clear();
  }

  get isEndOfList(): boolean {
    return this.pageInfo ? !this.pageInfo.hasNextPage : false;
  }

  getSortFieldValue(field: JobOrderByOption) {
    if (this.sortField !== field)
      return 'none';
    return this.sortOrder!;
  }

  setSortFieldValue(field: JobOrderByOption, val?: SortFieldValue, fetch: boolean = true) {
    if (!val || val === 'none') {
      this.sortField = null;
      this.sortOrder = null;
    } else {
      this.sortField = field;
      this.sortOrder = val;
    }

    if (fetch && this.sortStorageKey)
      this.store.storage.setLocal(this.sortStorageKey, { sortField: this.sortField, sortOrder: this.sortOrder }, true);

    if (fetch)
      this.debouncedFetch();
  }

  restoreSort() {
    const saved = this.sortStorageKey &&
      this.store.storage.getLocal<{ sortField: JobOrderByOption | null, sortOrder: Order | null }>(this.sortStorageKey, true);
    if (!saved)
      return;
    this.sortField = saved.sortField;
    this.sortOrder = saved.sortOrder;
  }

  getFilterFieldValue<T extends keyof GetJobsFilterInput>(field: T): string | null {
    switch (field) {
      case 'videoType':
        return this.videoTypeFilter;
      case 'status':
        return this.statusFilter;
    }
    return null;
  }

  getLiveStatusFilterValue(): JobLiveAggregateStatus | null {
    return this.liveStatusFilter;
  }

  setFilterFieldValue<T extends keyof GetJobsFilterInput>(field: T, val?: Maybe<GetJobsFilterInput[T]>, fetch: boolean = true) {
    switch (field) {
      case 'sourceType':
        this.sourceTypeFilter = (val as JobSourceType) || null;
        break;
      case 'videoType':
        this.videoTypeFilter = (val as string) || null;
        break;
      case 'status':
        this.statusFilter = (val as JobStatus) || null;
        break;
      case 'tags':
        this.tagsFilter = (val as string[]) || null;
    }

    if (fetch)
      this.debouncedFetch();
  }

  setLiveStatusFilterValue(liveStatusFilter: JobLiveAggregateStatus | null) {
    this.liveStatusFilter = liveStatusFilter;
    this.debouncedFetch();
  }

  handleSearchInputChange(evt: InputState) {
    this.searchFilter = evt.value || null;
    this.debouncedFetch();
  }

  debouncedFetch = debounce(this.fetch, 500);

  setPagesizeValue(pageSize: number) {
    if (this.pageSize !== pageSize) {
      this.pageSize = pageSize;
      this.debouncedFetch();
    }
  }

  getVideoTypeFilterValue(): JobType | null {
    return this.videoTypeFilter as JobType;
  }
  getVideoTypeFilterLabel() {
    return getVideoTypeLabel(this.getVideoTypeFilterValue());
  }

  setVideoTypeFilterValue(val?: Maybe<string>) {
    this.videoTypeFilter = val || null;
    this.fetch();
  }


  /** Makes a fresh fetch call, clearing any previous data and pagination state. */
  async fetch()
    : AsyncResult<JobCatalogItem[]> {
    this.syncStatus = 'fetching';
    this.transientJobsReaction ??= reaction(() => this.transientEntities.length, action(() => {
      this.transientJobRefetches = 0;
      this.syncProcessingJobsRefresh();
    }));

    const [jobsRes, err] = await this.fetchRaw();

    if (err)
      return [null, err];

    assertNotNull(jobsRes);

    const retItems: JobCatalogItem[] = [];
    runInAction(() => {
      this.items.clear();

      const { getJobs } = jobsRes;
      this.pageInfo = getJobs.pageInfo;

      getJobs.edges.forEach((edge, i) => {

        const item = new JobCatalogItem(this.store, {
          jobId: edge.node.id,
          cursor: edge.cursor,
          index: i
        });

        this.items.push(item);
        retItems.push(item);

        if (this.transientEntityIds.has(item.jobId))
          console.warn(`JobCatalogSource: Job ${item.jobId} is already registered as a transient entity which means it was not properly removed within the JobManager after the fetch.`);
      });
    });
    this.syncProcessingJobsRefresh();

    return [retItems];
  }

  async fetchMore()
    : AsyncResult<JobCatalogItem[]> {

    this.syncStatus = 'fetchingMore';

    const [jobsRes, err] = await this.fetchRaw(true);
    if (err)
      return [null, err];

    assertNotNull(jobsRes);

    const retItems: JobCatalogItem[] = [];
    runInAction(() => {
      const { getJobs } = jobsRes;
      this.pageInfo = getJobs.pageInfo;

      getJobs.edges.forEach((edge, i) => {

        const item = new JobCatalogItem(this.store, {
          jobId: edge.node.id,
          cursor: edge.cursor,
          index: i
        });

        this.items.push(item);
        retItems.push(item);

        if (this.transientEntityIds.has(item.jobId))
          console.warn(`JobCatalogSource: Job ${item.jobId} is already registered as a transient entity which means it was not properly removed within the JobManager after the fetch.`);
      });
    });
    this.syncProcessingJobsRefresh();

    return [retItems];
  }

  private getFetchArgs(more: boolean = false) {
    const args: ApiVariables<'getJobs'> = {
      posterSize: [JobsPosterSize]
    };
    const filter: GetJobsFilterInput = {};

    if (this.sortField && this.sortOrder) {
      args.sort = {
        field: this.sortField,
        order: this.sortOrder
      }
    }

    if (this.createdByIdFilter)
      filter.createdById = this.createdByIdFilter;
    if (this.videoTypeFilter)
      filter.videoType = this.videoTypeFilter;
    if (this.sourceTypeFilter)
      filter.sourceType = this.sourceTypeFilter;

    if (this.liveStatusFilter) {
      switch (this.liveStatusFilter) {
        case JobLiveAggregateStatus.NotReady: {
          filter.status = {
            eq: JobStatus.Pending
          }
          break;
        }
        case JobLiveAggregateStatus.Waiting: {
          filter.status = {
            eq: JobStatus.LiveReady
          }
          break;
        }
        case JobLiveAggregateStatus.Streaming: {
          filter.status = {
            eq: JobStatus.LiveStreaming
          }
          break;
        }
        case JobLiveAggregateStatus.Processing: {
          filter.status = {
            eq: JobStatus.InProgress
          }
          break;
        }
        case JobLiveAggregateStatus.LiveEnded: {
          filter.status = {
            eq: JobStatus.LiveEnded
          }
          break;
        }
        case JobLiveAggregateStatus.ProcessingDone: {
          filter.status = {
            in: [
              JobStatus.Done,
              JobStatus.Updated,
              JobStatus.InReview,
              JobStatus.Waiting]
          }
          break;
        }
        case JobLiveAggregateStatus.ProcessingFailed: {
          filter.status = {
            eq: JobStatus.Failed
          }
          break;
        }
      }
    }

    if (this.teamId)
      filter.teamId = this.teamId;

    if (this.allJobs)
      filter.allJobs = true;

    if (this.searchFilter) {
      filter._search = this.searchFilter || undefined;
    }

    const metadata = getCatalogMetadataFilterInput(this.metadataFilter);
    if (metadata && metadata.length > 0)
      filter.metadata = metadata;

    const tags = getCatalogTagsFilterInput(this.tagsFilter);
    if (tags && tags.length > 0)
      filter.tags = tags;

    if (this.folderId)
      filter.folderId = this.folderId;
    else if (this.resolvedProps.folders && !this.hasRefinement)
      filter.unfiled = true;

    args.filter = filter;
    args.first = this.pageSize;

    if (more && this.lastItemCursor)
      args.after = this.lastItemCursor;

    return args;
  }

  private async fetchRaw(more: boolean = false): ApiResult<'getJobs'> {

    const args: ApiVariables<'getJobs'> = this.getFetchArgs(more);
    const { jobManager } = this.store;

    const [jobsRes, err] = await jobManager.apiFetchJobs(args, {
      rawResponse: true
    });

    if (err) {
      runInAction(() => {
        this.syncStatus = 'error';
        // notifyError(this, `Could not fetch videos because of an error.`);
      });
      return [null, err];
    }

    runInAction(() => {
      this.syncStatus = 'idle';
    });

    assertNotNull(jobsRes);
    return [jobsRes];
  }

  reset() {
    this.cancelProcessingJobsRefresh();
    this.transientJobsReaction?.();
    this.transientJobsReaction = null;
    this.items.clear();
    this.pageInfo = null;
    this.searchFilter = null;

    this.statusFilter = null;
    this.liveStatusFilter = null;

    this.videoTypeFilter = null;
    this.createdByIdFilter = null;
    this.sourceTypeFilter = null;

    this.metadataFilter = null;
    this.tagsFilter = null;
    this.folderId = null;

    this.sortField = 'createdAt';
    this.sortOrder = 'desc';
    this.searchBarModel.clear();
  }

  // A just-uploaded job stays transient ("preparing") until a getJobs response includes it, so a
  // loaded list keeps refetching until the search index has it (QA BUG-022).
  private get isWaitingForTransientJobs(): boolean {
    return !!this.pageInfo && this.transientEntities.length > 0
      && this.transientJobRefetches < MAX_TRANSIENT_JOB_REFETCHES;
  }

  private syncProcessingJobsRefresh() {
    if (this.processingJobIds.length || this.isWaitingForTransientJobs)
      this.processingJobsRefreshTimer.start();
    else
      this.cancelProcessingJobsRefresh();
  }

  private cancelProcessingJobsRefresh() {
    this.processingJobsRefreshTimer.cancel();
    this.processingJobsAbortController?.abort();
    this.processingJobsAbortController = null;
    this.isRefreshingProcessingJobs = false;
  }

  private async refreshProcessingJobs() {
    if (this.isRefreshingProcessingJobs) {
      this.syncProcessingJobsRefresh();
      return;
    }

    if (this.isWaitingForTransientJobs) {
      this.isRefreshingProcessingJobs = true;
      runInAction(() => this.transientJobRefetches++);
      const [, err] = await this.fetch();
      this.isRefreshingProcessingJobs = false;
      if (err)
        this.syncProcessingJobsRefresh();
      return;
    }

    const jobIds = this.processingJobIds;
    if (!jobIds.length) {
      this.cancelProcessingJobsRefresh();
      return;
    }

    const abortController = new AbortController();
    this.processingJobsAbortController = abortController;
    this.isRefreshingProcessingJobs = true;

    try {
      await Promise.all(jobIds.map(jobId => this.store.jobManager.apiFetchJob(
        jobId,
        false,
        {
          signal: abortController.signal,
          suppressRedirectWhenOffline: true,
        },
        this.teamId ?? undefined,
      )));
    } catch {
      // Polling is best-effort; keep the catalog usable and try again on the next tick.
    } finally {
      this.isRefreshingProcessingJobs = false;
      if (this.processingJobsAbortController === abortController)
        this.processingJobsAbortController = null;

      if (!abortController.signal.aborted)
        this.syncProcessingJobsRefresh();
    }
  }

  private _verifyEntityDuplicates() {

    const transEntIds = this.transientEntityIds;
    const itemEntIds = this.itemEntityIds;

    const duplicates = new Set<string>();
    for (const transEntId of transEntIds) {
      if (itemEntIds.has(transEntId)) {
        duplicates.add(transEntId);
      }
    }

    if (duplicates.size > 0) {
      console.warn(
        `JobCatalogSource: Encountered ${duplicates.size} duplicates between fetched jobs and transient jobs:\n` +
        [...duplicates].map(id => `\t${id}\n`));
    }
  }
}
